import { MatrixDao } from '@/dao';
import { MatrixShootingCellWarningsAcknowledgement, DbDataChangedNotification, IntArray } from '@/objects/generated';
import { LoaderWithReset } from '@/utilities/LoaderWithReset';
import { defineStore } from 'pinia';
import { useEventStore } from './eventStore';
import { watch } from 'vue';
import signalrSubscribe from '@/objects/generated/signalr/signalrSubscribe';

type LoaderValueType = {
    matrixShootingCellWarningsAcknowledgement: MatrixShootingCellWarningsAcknowledgement[];
};

interface MatrixShootingCellWarningsAcknowledgementSubscriptionOptions {
    triggerForExistingResults?: boolean;
}

export const useMatrixShootingCellWarningsAcknowledgementStore = defineStore('matrixShootingCellWarningsAcknowledgementstore', () => {
    const eventStore = useEventStore();

    /**
     * List of all the active subscriptions
     */
    const matrixShootingCellWarningsAcknowledgementSubscriptions = new Set<(sr: MatrixShootingCellWarningsAcknowledgement[]) => void>();

    const loader = new LoaderWithReset<LoaderValueType>({
        defaultValue: { matrixShootingCellWarningsAcknowledgement: [] },
        loaderFunction: async () => {
            const matrixShootingCellWarningsAcknowledgement = await MatrixDao.GetMatrixShootingCellWarningsAcknowledgementsForEvent(
                eventStore.eventID,
            );

            return {
                matrixShootingCellWarningsAcknowledgement: matrixShootingCellWarningsAcknowledgement,
            };
        },
        waitOnBeforeLoad: eventStore.initpromise,
    });

    const reset = () => loader.Reset();

    const ensureLoaded = async () => await loader.Load();

    let signalrUnsubscribeFunction: Function | null = null;
    /**
     * This will trigger every time the eventID changes so we set up the loaders again
     */
    watch(
        () => eventStore.eventID,
        async (nv, ov) => {
            if (nv == ov || nv == 0) {
                return;
            }

            reset();

            if (signalrUnsubscribeFunction != null) {
                await signalrUnsubscribeFunction();
            }

            signalrUnsubscribeFunction = await signalrSubscribe.DbDataChangedNotification(
                {
                    DtoType: 'MatrixShootingCellWarningsAcknowledgement',
                },
                async (notifications) => {
                    if (notifications.some((n) => n.Operation !== 'Insert')) {
                        console.warn(
                            'We got a change in MatrixShootingCellWarningsAcknowledgement that was not an insert. This is unexpected!',
                        );
                    }
                    await handleNewMatrixShootingCellWarningsAcknowledgement(notifications);
                },
            );
        },
        { immediate: true },
    );

    const handleNewMatrixShootingCellWarningsAcknowledgement = async (notifications: DbDataChangedNotification[]) => {
        if (!notifications.length) {
            return;
        }

        // Note, we might load none here, if they are for another event. We dont know the event until we run the query in the dao
        const newAcks = await MatrixDao.GetMatrixShootingCellWarningsAcknowledgementsForEvent(
            eventStore.eventID,
            IntArray.FromJsValues({
                Values: notifications.map((s) => s.ID!),
            }),
        );
        loader.Value.matrixShootingCellWarningsAcknowledgement.push(...newAcks);

        matrixShootingCellWarningsAcknowledgementSubscriptions.forEach((cb) => cb(newAcks));
    };

    const subscribeToMatrixShootingCellWarningsAcknowledgements = (
        callback: (sr: MatrixShootingCellWarningsAcknowledgement[]) => void,
        options?: MatrixShootingCellWarningsAcknowledgementSubscriptionOptions,
    ) => {
        matrixShootingCellWarningsAcknowledgementSubscriptions.add(callback); // Note, we dont save the options since we never need to access the options again

        // If we should trigger for existing values, do so now
        if (options?.triggerForExistingResults) {
            ensureLoaded().then(() => {
                callback(loader.Value.matrixShootingCellWarningsAcknowledgement);
            });
        }

        // If the calling component is unmounted we need to remove the subscription
        return () => {
            matrixShootingCellWarningsAcknowledgementSubscriptions.delete(callback);
        };
    };

    return {
        /**
         * Ensure that all the data will be loaded from the server
         */
        EnsureLoaded: ensureLoaded,

        /**
         * Subscribe to acknowledgements. The callback will be invoked every time we get a new acknowledgement
         */
        subscribeToMatrixShootingCellWarningsAcknowledgements,

        /**
         * Gets all loaded acknowledgements.
         * @returns All loaded acknowledgements
         */
        getAllAcknowledgements: () => loader.Value.matrixShootingCellWarningsAcknowledgement,
    };
});
